import React from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '../../../lib/motion';
import { useImageUrl } from '../../../services/uploads';
import { isPending } from '../../courses/editors/contentPlaceholder';
import ModuleViewContainer from './ModuleViewContainer';

export interface ImageViewProps {
  item: { image_url?: string; alt_text?: string; caption?: string; title?: string };
  mode: 'learner' | 'preview';
}

const ImageView: React.FC<ImageViewProps> = ({ item, mode }) => {
  const [resolvedUrl, loading] = useImageUrl(
    item.image_url && !isPending(item.image_url) ? item.image_url : null
  );
  const isLearner = mode === 'learner';

  let body: React.ReactNode;
  if (!resolvedUrl && !loading) {
    body = (
      <div
        className={`w-full flex items-center justify-center rounded-lg bg-surface-secondary border border-border ${
          isLearner ? 'flex-1 min-h-0' : 'h-48'
        }`}
      >
        <span className="text-sm text-text-tertiary">No image</span>
      </div>
    );
  } else if (isLearner) {
    body = (
      <div className="flex-1 min-h-0 w-full flex flex-col gap-2">
        <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
          {loading ? (
            <div className="w-full h-full rounded-lg bg-surface-secondary animate-pulse" />
          ) : (
            <img
              src={resolvedUrl!}
              alt={item.alt_text ?? ''}
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg border border-border"
            />
          )}
        </div>
        {item.caption && (
          <p className="text-sm text-text-secondary italic text-center shrink-0">{item.caption}</p>
        )}
      </div>
    );
  } else {
    body = (
      <div className="w-full space-y-2">
        {loading ? (
          <div className="w-full h-48 rounded-lg bg-surface-secondary animate-pulse" />
        ) : (
          <img
            src={resolvedUrl!}
            alt={item.alt_text ?? ''}
            className="max-w-full h-auto rounded-lg border border-border mx-auto"
          />
        )}
        {item.caption && (
          <p className="text-sm text-text-secondary italic text-center">{item.caption}</p>
        )}
      </div>
    );
  }

  if (isLearner) {
    return (
      <motion.div {...fadeIn} className="w-full flex-1 flex flex-col min-h-0">
        <ModuleViewContainer padding="px-4 pb-4">
          {item.title && (
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                {item.title}
              </h2>
            </div>
          )}
          <div className="flex-1 min-h-0 flex flex-col px-3 sm:px-4 pb-3 sm:pb-4">
            {body}
          </div>
        </ModuleViewContainer>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} className="w-full">
      {body}
    </motion.div>
  );
};

export default ImageView;
